import { Box, Button, Stack } from "@mui/material";
import { useCallback, useState } from "react";
import type { MetricDataType } from "@entrilia/shared/api/portfolioMonitoringTypes";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import RecordCounter from "@entrilia/shared/components/filters/RecordCounter";
import SearchField from "@entrilia/shared/components/inputs/SearchField";
import useFetch from "@entrilia/shared/hooks/useFetch";
import adminApi from "../../../../api/adminApi";
import type { Metric } from "../../../../api/types/portfolioMonitoringTypes";
import useClientContext from "../../../../context/useClientContext";
import MetricsGrid from "./MetricsGrid";
import { MetricsPageContextProvider } from "./MetricsPageContextProvider";
import AddMetricDialog from "./metrics-edit/AddMetricDialog";
import DeactivateMetricDialog from "./metrics-edit/DeactivateMetricDialog";
import DeleteMetricDialog from "./metrics-edit/DeleteMetricDialog";
import EditMetricDialog from "./metrics-edit/EditMetricDialog";
import MetricDataTypeMenu from "./metrics-edit/MetricDataTypeMenu";

interface DialogState {
  openDialog?: "select_data_type" | "create_metric" | "edit_metric" | "delete_metric" | "deactivate_metric";
  newMetricDataType?: MetricDataType;
  editedMetric?: Metric;
}

interface Props {
  displayFilter: "active" | "inactive";
}

const MetricsTab = ({ displayFilter }: Props) => {
  const { hasPermissions } = useClientContext();

  const [newMetricButtonEl, setNewMetricButtonEl] = useState<HTMLButtonElement | null>(null);

  const [dialogState, setDialogState] = useState<DialogState>({});
  const [searchTerm, setSearchTerm] = useState("");

  const isInactive = displayFilter === "inactive";

  const getMetrics = useCallback(() => adminApi.getAllMetrics(isInactive), [isInactive]);
  const [metricsResp, fetchError, { isFetching, setData: setMetrics }] = useFetch(getMetrics);

  if (fetchError) {
    return <DataLoadingFailed title="Could not load metrics" />;
  }

  const hasEditPermissions = hasPermissions(["ManagePortfolioMonitoring"]);

  const isLoading = isFetching || metricsResp === undefined;

  const metrics = metricsResp ?? [];

  const handleSearch = (value: string) => {
    setSearchTerm(value.toLowerCase().trim());
  };

  const handleDataTypeSelect = (newMetricDataType: MetricDataType) => {
    setDialogState({ openDialog: "create_metric", newMetricDataType });
  };

  const handleAddMetric = () => {
    setDialogState({ openDialog: "select_data_type" });
  };

  const handleEditMetric = (editedMetric: Metric) => {
    setDialogState({ openDialog: "edit_metric", editedMetric });
  };

  const handleDeleteMetric = (deletedMetric: Metric) => {
    setDialogState({ openDialog: "delete_metric", editedMetric: deletedMetric });
  };

  const handleDeactivateMetric = (deactivatedMetric: Metric) => {
    setDialogState({ openDialog: "deactivate_metric", editedMetric: deactivatedMetric });
  };

  const handleMetricCreated = (newMetric: Metric) => {
    setMetrics((prev) => [newMetric, ...(prev ?? [])]);
    setDialogState({});
  };

  const handleMetricUpdated = (updatedMetric: Metric) => {
    // A metric whose Inactive flag no longer matches this tab moves to the other tab.
    setMetrics((prev) =>
      updatedMetric.inactive === isInactive
        ? (prev ?? []).map((metric) => (metric.id === updatedMetric.id ? updatedMetric : metric))
        : (prev ?? []).filter((metric) => metric.id !== updatedMetric.id)
    );
    setDialogState({});
  };

  const handleMetricDeleted = (deletedMetric: Metric) => {
    setMetrics((prev) => (prev ?? []).filter((metric) => metric.id !== deletedMetric.id));
    setDialogState({});
  };

  const filteredMetrics =
    searchTerm.length === 0
      ? metrics
      : metrics.filter(
          (metric) =>
            metric.id.toLowerCase() === searchTerm ||
            metric.name.toLowerCase().includes(searchTerm) ||
            metric.entryNo.toLowerCase().includes(searchTerm) ||
            (metric.category && metric.category.toLowerCase().includes(searchTerm))
        );

  return (
    <MetricsPageContextProvider
      hasEditPermissions={hasEditPermissions}
      metrics={metrics}
      onEditMetric={handleEditMetric}
      onDeleteMetric={handleDeleteMetric}
      onDeactivateMetric={handleDeactivateMetric}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
        <Box sx={{ display: "flex", width: "100%", justifyContent: "space-between", alignItems: "center", mb: 2.5 }}>
          <RecordCounter records={filteredMetrics.length} totalRecords={metrics.length} hide={isLoading} />
          <Stack direction="row" spacing={1}>
            <SearchField initialValue={searchTerm} debounceTimeMs={300} onSearch={handleSearch} />
            {hasEditPermissions && !isInactive && (
              <Button
                ref={(el) => setNewMetricButtonEl(el)}
                variant="contained"
                disabled={dialogState.openDialog !== undefined}
                onClick={handleAddMetric}
              >
                Add Metric
              </Button>
            )}
          </Stack>
        </Box>

        <MetricsGrid metrics={filteredMetrics} isLoading={isLoading} />
      </Box>
      <MetricDataTypeMenu
        anchorEl={newMetricButtonEl}
        open={dialogState.openDialog === "select_data_type"}
        onDataTypeSelect={handleDataTypeSelect}
        onClose={() => setDialogState({})}
      />
      {dialogState.openDialog === "create_metric" && dialogState.newMetricDataType && (
        <AddMetricDialog
          dataType={dialogState.newMetricDataType}
          onClose={() => setDialogState({})}
          onCreated={handleMetricCreated}
        />
      )}
      {dialogState.openDialog === "edit_metric" && dialogState.editedMetric && (
        <EditMetricDialog
          metric={dialogState.editedMetric}
          onClose={() => setDialogState({})}
          onUpdated={handleMetricUpdated}
        />
      )}
      {dialogState.openDialog === "delete_metric" && dialogState.editedMetric && (
        <DeleteMetricDialog
          metric={dialogState.editedMetric}
          onClose={() => setDialogState({})}
          onDeleted={handleMetricDeleted}
        />
      )}
      {dialogState.openDialog === "deactivate_metric" && dialogState.editedMetric && (
        <DeactivateMetricDialog
          metric={dialogState.editedMetric}
          onClose={() => setDialogState({})}
          onDeactivated={handleMetricUpdated}
        />
      )}
    </MetricsPageContextProvider>
  );
};

export default MetricsTab;
